// Which nav entry owns the current route. Same rules as the old rail (src/components/sidebar/links-group.tsx),
// which keeps them private, plus one fix: links that differ only by query (Berita's sources) now light up
// the one whose query matches instead of always the first.

/**
 * A link matches its own path and its descendant routes: `/foo` covers `/foo/123` but never `/foobar`.
 * The query is ignored here; `activeLinkIndex` uses it to break ties.
 */
export function matchesPath(pathname: string, link: string): boolean {
  const base = link.split('?')[0];
  if (!pathname || !base || base === '#') return false;
  if (base === '/') return pathname === '/';
  return pathname === base || pathname.startsWith(`${base}/`);
}

/** Every key=value in the link's query is present in the current search. */
function queryMatches(search: string, link: string): boolean {
  const query = link.split('?')[1];
  if (!query) return false;
  const current = new URLSearchParams(search);
  for (const [key, value] of new URLSearchParams(query)) {
    if (current.get(key) !== value) return false;
  }
  return true;
}

/**
 * Index of the sibling link that owns the route, or -1. Siblings nest (`/customer-approval` is the parent
 * path of `/customer-approval/email-groups`), so the longest path wins; on a tie the one whose query
 * matches wins, else the first.
 */
export function activeLinkIndex(pathname: string, search: string, links: ReadonlyArray<{ link: string }>): number {
  let best = -1;
  let bestScore = -1;
  links.forEach(({ link }, index) => {
    if (!matchesPath(pathname, link)) return;
    const score = link.split('?')[0].length * 2 + (queryMatches(search, link) ? 1 : 0);
    if (score > bestScore) {
      bestScore = score;
      best = index;
    }
  });
  return best;
}

/** A top-level link (no sub-links): its own path or a descendant, except `/` and `/doc-pabean` (old rule). */
export function isLeafActive(pathname: string, link: string): boolean {
  if (pathname === link) return true;
  return link !== '/' && link !== '/doc-pabean' && link !== '#' && pathname.startsWith(`${link}/`);
}
